import { Vector3 } from "three";
import { CYLINDER_FIT_CONFIG } from "./cylinder-fit";
import {
  deserializeCylinder,
  serializeCylinder,
  type SerializedCylinder,
} from "./cylinder-fitting";
import type { PipeFrontier, PipeStep, PipeTransition } from "./pipe-run";
import type { CylinderSurface } from "./cylinder-surface";
import type { PipeEvidence, PipeTraceSolver } from "./pipe-tracker";

const FIT_TIMEOUT = 10_000;

export type SerializedPipeFrontier = {
  center: number[];
  axis: number[];
  radius: number;
};
type SerializedPipeStep = {
  frontier: SerializedPipeFrontier;
  cylinder: SerializedCylinder;
  indices: Uint32Array;
  normals: Float32Array;
  tolerance: number;
};
type PipeWorkerAction =
  | { operation: "step" | "transition"; frontier: SerializedPipeFrontier }
  | { operation: "seed"; clicked: number[] };
export type PipeWorkerRequest = {
  id: number;
  positions: Float64Array;
  spacing: number;
} & PipeWorkerAction;
export type PipeWorkerResponse = { id: number } & (
  | { operation: "seed"; cylinder?: SerializedCylinder }
  | { operation: "step"; step?: SerializedPipeStep }
  | {
      operation: "transition";
      kind: PipeTransition["kind"];
      outlets: SerializedPipeStep[];
      indices: Uint32Array;
      normals: Float32Array;
    }
  | { error: string }
);

/**
 * @param frontier Current tracing frontier.
 * @returns Clone-safe coordinates.
 */
export function serializePipeFrontier(
  frontier: PipeFrontier,
): SerializedPipeFrontier {
  return {
    center: frontier.center.toArray(),
    axis: frontier.axis.toArray(),
    radius: frontier.radius,
  };
}

/**
 * @param frontier Clone-safe frontier.
 * @returns Owned vectors.
 */
export function deserializePipeFrontier(
  frontier: SerializedPipeFrontier,
): PipeFrontier {
  return {
    center: new Vector3().fromArray(frontier.center),
    axis: new Vector3().fromArray(frontier.axis),
    radius: frontier.radius,
  };
}

/**
 * @param samples Observed members.
 * @param indices Input point identity map.
 * @returns Bounded copied membership and normals.
 */
export function serializePipeSamples(
  samples: PipeStep["samples"],
  indices: Map<Vector3, number>,
): Pick<SerializedPipeStep, "indices" | "normals"> {
  return {
    indices: new Uint32Array(
      samples.map(({ point }) => {
        const index = indices.get(point);
        if (index === undefined) {
          throw new Error("Unknown pipe evidence identity");
        }
        return index;
      }),
    ),
    normals: new Float32Array(
      samples.flatMap(({ normal }) => normal.toArray()),
    ),
  };
}

/**
 * @param step Accepted local fit.
 * @param indices Input identity map.
 * @returns Clone-safe fit and observed membership.
 */
export function serializePipeStep(
  step: PipeStep,
  indices: Map<Vector3, number>,
): SerializedPipeStep {
  return {
    frontier: serializePipeFrontier(step.frontier),
    cylinder: serializeCylinder(step.cylinder),
    tolerance: step.tolerance,
    ...serializePipeSamples(step.samples, indices),
  };
}

function deserializeSamples(
  encoded: Pick<SerializedPipeStep, "indices" | "normals">,
  evidence: PipeEvidence,
): PipeStep["samples"] {
  return Array.from(encoded.indices, (index, offset) => {
    if (index >= evidence.points.length) {
      throw new Error("Invalid pipe membership index");
    }
    const point = evidence.points[index];
    return {
      point: point.clone(),
      normal: new Vector3().fromArray(encoded.normals, offset * 3),
      spacing: evidence.spacing,
      supportSpacing: evidence.supportSpacing,
    };
  });
}

function deserializeStep(
  encoded: SerializedPipeStep,
  evidence: PipeEvidence,
): PipeStep {
  return {
    cylinder: deserializeCylinder(encoded.cylinder),
    frontier: deserializePipeFrontier(encoded.frontier),
    tolerance: encoded.tolerance,
    samples: deserializeSamples(encoded, evidence),
  };
}

/** Owns a serial fitting worker for one tracing job, including synchronous solver cancellation. */
export class PipeTrackerWorkerClient implements PipeTraceSolver {
  private sequence = 0;
  private disposed = false;
  private pending?: {
    id: number;
    resolve(response: PipeWorkerResponse): void;
    reject(error: Error): void;
  };

  /**
   * @param worker Owned module worker.
   * @param signal Source/selection lifetime.
   */
  constructor(
    private readonly worker: Worker,
    private readonly signal: AbortSignal,
  ) {
    worker.addEventListener("message", this.message);
    worker.addEventListener("error", this.failed);
    worker.addEventListener("messageerror", this.failed);
    signal.addEventListener("abort", this.dispose, { once: true });
    if (signal.aborted) this.dispose();
  }

  private message = (event: MessageEvent<PipeWorkerResponse>): void => {
    if (this.disposed || event.data.id !== this.pending?.id) return;
    if ("error" in event.data) {
      this.pending.reject(new Error(event.data.error));
      this.dispose();
    } else {
      this.pending.resolve(event.data);
    }
  };

  private failed = (): void => {
    this.pending?.reject(new Error("Pipe tracking worker failed or timed out"));
    this.dispose();
  };

  private async request(
    evidence: PipeEvidence,
    cancelled: () => boolean,
    action: PipeWorkerAction,
  ): Promise<PipeWorkerResponse> {
    if (this.disposed || cancelled()) {
      throw new DOMException("Pipe tracking cancelled", "AbortError");
    }
    if (this.pending) throw new Error("Pipe computation already in progress");
    if (evidence.points.length > CYLINDER_FIT_CONFIG.maximumSamples) {
      throw new Error("Pipe evidence exceeds worker limit");
    }
    const positions = new Float64Array(evidence.points.length * 3);
    evidence.points.forEach((point, index) =>
      point.toArray(positions, index * 3),
    );
    const request: PipeWorkerRequest = {
      id: ++this.sequence,
      ...action,
      positions,
      spacing: evidence.spacing,
    };
    const timeout = setTimeout(this.failed, FIT_TIMEOUT);
    try {
      const response = await new Promise<PipeWorkerResponse>(
        (resolve, reject) => {
          this.pending = { id: request.id, resolve, reject };
          this.worker.postMessage(request, [positions.buffer]);
        },
      );
      if (cancelled()) {
        throw new DOMException("Pipe tracking cancelled", "AbortError");
      }
      return response;
    } finally {
      clearTimeout(timeout);
      this.pending = undefined;
    }
  }

  /**
   * @param evidence Bounded source points.
   * @param frontier Current end.
   * @param cancelled Generation check.
   * @returns Supported step.
   */
  async step(
    evidence: PipeEvidence,
    frontier: PipeFrontier,
    cancelled: () => boolean,
  ): Promise<PipeStep | undefined> {
    const response = await this.request(evidence, cancelled, {
      operation: "step",
      frontier: serializePipeFrontier(frontier),
    });
    if (!("operation" in response) || response.operation !== "step") {
      throw new Error("Invalid pipe step response");
    }
    return response.step ? deserializeStep(response.step, evidence) : undefined;
  }

  /**
   * @param evidence Bounded source points.
   * @param frontier Current end.
   * @param cancelled Generation check.
   * @returns Connected fitting and supported outlets.
   */
  async transition(
    evidence: PipeEvidence,
    frontier: PipeFrontier,
    cancelled: () => boolean,
  ): Promise<PipeTransition> {
    const response = await this.request(evidence, cancelled, {
      operation: "transition",
      frontier: serializePipeFrontier(frontier),
    });
    if (!("operation" in response) || response.operation !== "transition") {
      throw new Error("Invalid pipe transition response");
    }
    return {
      kind: response.kind,
      outlets: response.outlets.map((outlet) =>
        deserializeStep(outlet, evidence),
      ),
      samples: deserializeSamples(response, evidence),
    };
  }

  /**
   * @param evidence Bounded source-filtered seed points.
   * @param clicked Location seed, not a pinned surface constraint.
   * @param cancelled Selection generation check.
   * @returns A validated adaptive 3D seed model.
   */
  async seed(
    evidence: PipeEvidence,
    clicked: Vector3,
    cancelled: () => boolean,
  ): Promise<CylinderSurface | undefined> {
    const response = await this.request(evidence, cancelled, {
      operation: "seed",
      clicked: clicked.toArray(),
    });
    if (!("operation" in response) || response.operation !== "seed") {
      throw new Error("Invalid pipe seed response");
    }
    return response.cylinder
      ? deserializeCylinder(response.cylinder)
      : undefined;
  }

  /** Releases listeners and terminates active numerical work. */
  readonly dispose = (): void => {
    if (this.disposed) return;
    this.disposed = true;
    this.pending?.reject(
      new DOMException("Pipe tracking cancelled", "AbortError"),
    );
    this.signal.removeEventListener("abort", this.dispose);
    this.worker.removeEventListener("message", this.message);
    this.worker.removeEventListener("error", this.failed);
    this.worker.removeEventListener("messageerror", this.failed);
    this.worker.terminate();
  };
}
